import React, { useState } from 'react'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import {
    ThumbUpOutlined as ThumbUpIcon,
    ThumbDownOutlined as ThumbDownIcon,
} from '@mui/icons-material'
import {
    submitPositiveFeedback,
    submitNegativeFeedback,
} from '../utils/trainingApi'

interface FeedbackButtonsProps {
    agentId: string
    conversationId: string
    messageId: string
    originalResponse?: string
}

type FeedbackState = 'idle' | 'submitting' | 'positive' | 'negative'

export const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({
    agentId,
    conversationId,
    messageId,
    originalResponse,
}) => {
    const [state, setState] = useState<FeedbackState>('idle')

    if (state === 'positive' || state === 'negative') {
        return (
            <Typography
                sx={{
                    fontSize: 11,
                    color:
                        state === 'positive'
                            ? 'var(--success-color, #22c55e)'
                            : 'var(--main-text-secondary, #64748b)',
                }}
            >
                {state === 'positive' ? 'Thanks for the feedback' : 'Feedback noted'}
            </Typography>
        )
    }

    const handlePositive = async () => {
        if (state !== 'idle') return
        setState('submitting')
        try {
            await submitPositiveFeedback(
                agentId,
                conversationId,
                messageId,
                originalResponse,
            )
            setState('positive')
        } catch {
            setState('idle')
        }
    }

    const handleNegative = async () => {
        if (state !== 'idle') return
        setState('submitting')
        try {
            await submitNegativeFeedback(
                agentId,
                conversationId,
                messageId,
                originalResponse,
            )
            setState('negative')
        } catch {
            setState('idle')
        }
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Helpful" slotProps={{ popper: { style: { zIndex: 100000 } } }}>
                <span>
                    <IconButton
                        size="small"
                        aria-label="Mark response as helpful"
                        disabled={state === 'submitting'}
                        onClick={() => {
                            void handlePositive()
                        }}
                        sx={{ color: 'var(--main-text-secondary, #64748b)' }}
                    >
                        <ThumbUpIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip title="Not helpful" slotProps={{ popper: { style: { zIndex: 100000 } } }}>
                <span>
                    <IconButton
                        size="small"
                        aria-label="Mark response as not helpful"
                        disabled={state === 'submitting'}
                        onClick={() => {
                            void handleNegative()
                        }}
                        sx={{ color: 'var(--main-text-secondary, #64748b)' }}
                    >
                        <ThumbDownIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </span>
            </Tooltip>
        </Box>
    )
}

export default FeedbackButtons
